(() => {
  if (customElements.get('jdm-sort-control')) return;

  class JdmSortControl extends HTMLElement {
    connectedCallback() {
      if (this.controller || typeof this.showPopover !== 'function') return;
      this.select = this.querySelector('select');
      if (!this.select || !this.select.options.length) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'jdm-sort-trigger select__select caption-large';
      this.trigger.id = `${this.select.id}-trigger`;
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-controls', `${this.select.id}-listbox`);
      this.trigger.setAttribute('aria-describedby', 'a11y-refresh-page-message');
      this.label = document.createElement('span');
      this.label.className = 'jdm-sort-trigger__label';
      const caret = document.createElement('span');
      caret.className = 'jdm-sort-trigger__caret';
      caret.setAttribute('aria-hidden', 'true');
      this.trigger.append(this.label, caret);
      this.listbox = document.createElement('div');
      this.listbox.className = 'jdm-sort-listbox custom-scrollbar';
      this.listbox.id = `${this.select.id}-listbox`;
      this.listbox.setAttribute('popover', 'manual');
      this.listbox.setAttribute('role', 'listbox');
      this.listbox.setAttribute('aria-label', this.dataset.label);
      this.listbox.tabIndex = -1;
      this.options = Array.from(this.select.options, (option, index) => {
        const item = document.createElement('div');
        item.id = `${this.select.id}-option-${index}`;
        item.className = 'jdm-sort-option';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-disabled', String(option.disabled));
        item.dataset.index = index;
        item.textContent = option.textContent;
        this.listbox.append(item);
        return item;
      });
      this.append(this.trigger, this.listbox);
      this.sync();
      this.trigger.addEventListener('click', () => this.opened ? this.close(true) : this.open(), { signal });
      this.trigger.addEventListener('keydown', event => {
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          this.open(event.key === 'Home' ? 0 : event.key === 'End' ? this.options.length - 1 : undefined);
        }
      }, { signal });
      this.listbox.addEventListener('keydown', event => this.onKeyDown(event), { signal });
      this.listbox.addEventListener('click', event => {
        const item = event.target.closest('[role="option"]');
        if (item && this.listbox.contains(item)) this.choose(Number(item.dataset.index));
      }, { signal });
      this.select.addEventListener('change', () => this.sync(), { signal });
      document.addEventListener('pointerdown', event => {
        if (this.opened && !this.contains(event.target)) this.autoClose();
      }, { signal });
      // After the popover closes by itself (outside tap, page scroll, resize), the click that
      // belongs to the same gesture must not land on whatever is underneath (a product card).
      document.addEventListener('click', event => {
        if (performance.now() - (this.autoClosedAt || -1e9) > 700) return;
        if (this.contains(event.target)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.autoClosedAt = -1e9;
      }, { signal, capture: true });
      document.addEventListener('focusin', event => {
        if (this.opened && !this.contains(event.target)) this.close(false);
      }, { signal });
      // Browser-chrome show/hide and card carousels fire resize/scroll while the menu is open.
      // Reposition (or ignore) those; only a real width change or a real page scroll closes it.
      this.openWidth = 0;
      const onViewportChange = () => {
        if (!this.opened) return;
        if (Math.abs(window.innerWidth - this.openWidth) > 1) this.autoClose();
        else this.position();
      };
      window.addEventListener('resize', onViewportChange, { signal });
      window.visualViewport?.addEventListener('resize', onViewportChange, { signal });
      window.addEventListener('scroll', event => {
        if (!this.opened || this.listbox.contains(event.target)) return;
        const pageScroll = event.target === document || event.target === document.documentElement || event.target === document.body;
        if (!pageScroll) return; // inner scrollers (card galleries, sliders) must not dismiss the menu
        if (Math.abs(window.scrollY - this.openScrollY) > 24) this.autoClose();
        else this.position();
      }, { signal, capture: true });
      this.select.hidden = true;
      this.dataset.enhanced = 'true';
    }

    disconnectedCallback() {
      this.close(false);
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.typeTimer);
      this.trigger?.remove();
      this.listbox?.remove();
      if (this.select) this.select.hidden = false;
      delete this.dataset.enhanced;
    }

    sync() {
      const selected = this.select.selectedIndex;
      const text = this.select.options[selected]?.textContent || '';
      this.label.textContent = this.dataset.compactLabel || text;
      this.trigger.setAttribute('aria-label', `${this.dataset.label} ${text}`);
      this.trigger.disabled = this.select.disabled;
      this.options.forEach((option, index) => option.setAttribute('aria-selected', String(index === selected)));
    }

    open(index = this.select.selectedIndex) {
      if (this.opened || this.select.disabled) return;
      document.querySelectorAll('jdm-sort-control').forEach(control => {
        if (control !== this) control.close?.(false);
      });
      this.sync();
      this.listbox.showPopover();
      this.opened = true;
      this.openWidth = window.innerWidth;
      this.openScrollY = window.scrollY;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.position();
      this.listbox.focus({ preventScroll: true });
      this.activate(this.enabledIndex(index, 1));
    }

    position() {
      const viewport = window.visualViewport;
      const leftEdge = viewport?.offsetLeft || 0;
      const topEdge = viewport?.offsetTop || 0;
      const width = viewport?.width || window.innerWidth;
      const height = viewport?.height || window.innerHeight;
      const rect = this.trigger.getBoundingClientRect();
      const gap = 6;
      const margin = 8;
      const panelWidth = Math.min(Math.max(rect.width, 260), width - margin * 2);
      this.listbox.style.width = `${panelWidth}px`;
      this.listbox.style.maxHeight = `${Math.min(350, height - margin * 2)}px`;
      const panelHeight = this.listbox.getBoundingClientRect().height;
      const below = topEdge + height - rect.bottom - gap - margin;
      const above = rect.top - topEdge - gap - margin;
      const useAbove = below < Math.min(panelHeight, 220) && above > below;
      const available = Math.max(44, useAbove ? above : below);
      this.listbox.style.maxHeight = `${Math.min(350, available, height - margin * 2)}px`;
      const finalHeight = this.listbox.getBoundingClientRect().height;
      const left = Math.max(leftEdge + margin, Math.min(rect.right - panelWidth, leftEdge + width - panelWidth - margin));
      const top = Math.max(topEdge + margin, Math.min(useAbove ? rect.top - finalHeight - gap : rect.bottom + gap, topEdge + height - finalHeight - margin));
      this.listbox.style.left = `${left}px`;
      this.listbox.style.top = `${top}px`;
    }

    enabledIndex(index, direction) {
      const count = this.options.length;
      for (let step = 0; step < count; step++) {
        const candidate = ((index + step * direction) % count + count) % count;
        if (!this.select.options[candidate].disabled) return candidate;
      }
      return -1;
    }

    activate(index) {
      if (index < 0) return;
      this.activeIndex = index;
      this.options.forEach((option, i) => option.classList.toggle('is-active', i === index));
      this.listbox.setAttribute('aria-activedescendant', this.options[index].id);
      this.options[index].scrollIntoView({ block: 'nearest' });
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
      } else if (event.key === 'Tab') {
        this.close(true);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.choose(this.activeIndex);
      } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const direction = event.key === 'ArrowUp' || event.key === 'End' ? -1 : 1;
        const start = event.key === 'Home' ? 0 : event.key === 'End' ? this.options.length - 1 : this.activeIndex + direction;
        this.activate(this.enabledIndex(start, direction));
      } else if (event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
        event.preventDefault();
        clearTimeout(this.typeTimer);
        this.typeBuffer = `${this.typeBuffer || ''}${event.key.toLocaleLowerCase()}`;
        const repeated = Array.from(this.typeBuffer).every(character => character === this.typeBuffer[0]);
        const query = repeated ? this.typeBuffer[0] : this.typeBuffer;
        const start = repeated ? this.activeIndex + 1 : this.activeIndex;
        for (let step = 0; step < this.options.length; step++) {
          const index = (start + step) % this.options.length;
          if (!this.select.options[index].disabled && this.options[index].textContent.trim().toLocaleLowerCase().startsWith(query)) {
            this.activate(index);
            break;
          }
        }
        this.typeTimer = setTimeout(() => { this.typeBuffer = ''; }, 700);
      }
    }

    choose(index) {
      if (index == null || index < 0 || !this.select.options[index] || this.select.options[index].disabled) return;
      const changed = this.select.selectedIndex !== index;
      this.select.selectedIndex = index;
      this.sync();
      this.close(true);
      if (changed) this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    autoClose() {
      if (!this.opened) return;
      this.autoClosedAt = performance.now();
      this.close(false);
    }

    close(returnFocus = false) {
      if (!this.opened) return;
      this.opened = false;
      if (this.listbox.matches(':popover-open')) this.listbox.hidePopover();
      this.trigger.setAttribute('aria-expanded', 'false');
      this.typeBuffer = '';
      clearTimeout(this.typeTimer);
      if (returnFocus && this.isConnected) this.focusTrigger();
    }

    focusTrigger() {
      this.trigger?.focus({ preventScroll: true });
    }

    static captureFocus() {
      return document.activeElement?.closest('jdm-sort-control')?.select?.id || null;
    }

    static restoreFocus(id) {
      const control = document.getElementById(id)?.closest('jdm-sort-control');
      if (!control?.trigger) return false;
      control.focusTrigger();
      return true;
    }
  }

  window.JdmSortControl = JdmSortControl;
  customElements.define('jdm-sort-control', JdmSortControl);
})();
